import { useCallback, useEffect, useRef, useState } from "react";
import TextareaAutosize from 'react-textarea-autosize';
import "emoji-mart/css/emoji-mart.css";
import { Picker } from "emoji-mart";
import { useParams } from "react-router";
import { v4 as uuidV4 } from 'uuid';
import GiphySelect from 'react-giphy-searchbox';
import jwt from 'jsonwebtoken';
import { useDispatch } from "react-redux";
import classNames from "classnames";

import { CreateLiveChatOrQuestionMessage } from "../../../../connection/save-chat-message";
import { BlProfile, BrandliveEvent, LanguagesAbbr, Session, SqsSocketTypes } from "../../../../types/working-model";
import useShiftEnter from "../../../../utils/use-shift-enter";
import Icon, { COLORS, ICONS } from "../../../general-ui/icon";
import { Popover } from "../../../general-ui/popover/popover";
import { GetUploadEndpoint, MakeItemPublic, UploadToS3 } from "../../../../connection/uploads";
import { useTypedSelector } from "../../../../store/reducers/use-typed-selector";
import { setBlUser, toggleRegistrationModal } from "../../../../store/actions/event/event-actions";
import { incomingChatMessage } from "../../../../store/actions/event/chat-actions";
import { getStorageItem } from "../../../../utils/local-storage";
import { useTranslation } from "react-i18next";
import React from "react";
import ReactDOM from "react-dom";

interface IAddLiveCommentProps {
	session: Session;
	language: LanguagesAbbr;
	setShowSpamFilterAlert: React.Dispatch<React.SetStateAction<boolean>>;
	showSpamFilterAlert: boolean;
	eventBundle: BrandliveEvent;
	isAdmin?: boolean;
}

interface IGif {
	title: string;
	url: string;
}

const AddLiveComment: React.FC<IAddLiveCommentProps> = ({
	session,
	eventBundle,
	language,
	showSpamFilterAlert,
	setShowSpamFilterAlert,
	isAdmin = false,
}) => {

	const { blProfileUser, registrationId, userSession, previewMode } = useTypedSelector(state => ({
		blProfileUser: state.LiveEventReducer.blProfileUser,
		registrationId: state.LiveEventReducer.registrationId,
		userSession: state.LiveEventReducer.userSession,
		previewMode: state.CreateEventReducer.previewMode
	}));

	const { event, channel, registration_on } = eventBundle;

	const [value, setValue] = useState('');
	const [submitting, setSubmitting] = useState(false);
	const [focusedIndex, setFocusedIndex] = useState(0);
	const [showPicker, setShowPicker] = useState(false);
	const [showGiphy, setShowGiphy] = useState(false);
	const [selectedGif, setSelectedGif] = useState<IGif | null>(null);
	const [imagePreview, setImagePreview] = useState('');
	const [selectedImageFile, setSelectedImageFile] = useState<any>(null);

	const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
	const imageTriggerRef = useRef<HTMLInputElement | null>(null);

	const { uuid } = useParams() as any;
	const { t } = useTranslation('session');
	const { isEnter } = useShiftEnter({
		ref: textAreaRef,
	});

	const dispatch = useDispatch();

	const token = getStorageItem('Bl-Profile-Token');

	useEffect(() => {
		if (token && !previewMode) {
			const result = jwt.decode(token);
			if (result) {
				// dispatch user 
				dispatch(setBlUser(result as BlProfile));
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [token, dispatch]);

	const saveMessage = useCallback(async () => {
		try {
			if (value.trim() === '' && !selectedImageFile && !selectedGif) {
				return;
			}
			textAreaRef.current?.focus();
			setSubmitting(true);
			let image: any = null;
			if (selectedImageFile) {

				const prefix = `uploads/${uuid}/${session.uuid}`;

				const endpoint = await GetUploadEndpoint(token!, prefix, selectedImageFile!.name, false, true);

				const handleUploadProgress = () => null;

				//upload to S3 - handle upload progress in separate function
				await UploadToS3(endpoint.url, selectedImageFile, endpoint.fields, handleUploadProgress, selectedImageFile.name);

				//make the item publicly accessible
				await MakeItemPublic(token!, endpoint.fields.Key, true);

				image = {
					url: `${endpoint.url}/${endpoint.fields.Key}`,
					name: selectedImageFile?.name,
				};
			}
			const body = {
				uuid: uuidV4(),
				comment: value.trimRight(),
				gif: selectedGif,
				image,
				session_uuid: session.uuid,
				// reason for having reply_to_id and also reply_to:
				// if a moderator deletes/blocks a message, we need an easy way to query any replies to that message
				// and set the content in the reply_to field to null
				reply_to_id: null, // if a reply, the id of the original message
				reply_to: null, // if a reply, the entire content of the original message
				channel,
				event,
				message_type: SqsSocketTypes.CHAT,
				language,
				registration_id: registration_on ? registrationId : null,
				user_session: registration_on ? userSession : null,
			};
			const result = await CreateLiveChatOrQuestionMessage(session.session, body, token!);
			dispatch(incomingChatMessage(result));

			URL.revokeObjectURL(selectedImageFile);
			ReactDOM.unstable_batchedUpdates(() => {
				setValue('');
				setSelectedGif(null);
				setImagePreview('');
				setSelectedImageFile(null);
				setSubmitting(false);
			});


		} catch (e) {
			console.log('ERROR');
			setSubmitting(false);
			console.error(e?.response || e);
		}
	}, [session, token, value, selectedGif, selectedImageFile, uuid, channel, event, registration_on, language, registrationId, userSession, dispatch]);

	useEffect(() => {
		const validContent = value.trim().length || selectedGif || selectedImageFile;
		if (!submitting && isEnter && session && validContent && token) {
			saveMessage();
		}
	}, [submitting, isEnter, session, token, value, selectedGif, selectedImageFile, uuid, channel, event, registration_on, language, registrationId, userSession, saveMessage]);

	const handleEmoji = (input: any) => {
		const updated = value.slice(0, focusedIndex) + (input?.native ?? '') + value.slice(focusedIndex);
		setValue(updated);
		// to keep the current focused index:
		// const updatedFocusedIndex = focusedIndex + (input?.native?.length || 0);
		// setFocusedIndex(updatedFocusedIndex);
		// if (textAreaRef?.current?.selectionStart) {
		// 	textAreaRef.current.selectionStart = updatedFocusedIndex;
		// }
		// else to set it to the end:
		setFocusedIndex(updated.length);
		if (textAreaRef?.current?.selectionStart) {
			textAreaRef.current.selectionStart = updated.length;
		}
		textAreaRef.current?.focus();
		setShowPicker(false);
	};

	const handleGifSelect = (input: any) => {
		setSelectedGif({
			title: input?.title || '',
			url: input?.images?.downsized_medium?.url
		});
		setShowGiphy(false);
		textAreaRef.current?.focus();
	};

	const handleImageTrigger = () => {
		if (imageTriggerRef?.current) {
			imageTriggerRef.current.click();
		}
	};

	const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (selectedImageFile) {
			URL.revokeObjectURL(selectedImageFile);
		}
		const file = e.target.files?.[0];
		if (file) {
			setSelectedImageFile(file);
			setImagePreview(URL.createObjectURL(file));
			e.target.value = '';
		}
		textAreaRef.current?.focus();
	};

	const handleRemoveImage = () => {
		URL.revokeObjectURL(selectedImageFile);
		setImagePreview('');
		setSelectedImageFile(null);
	};

	const handleNotRegistered = () => {
		if (token) return;
		dispatch(toggleRegistrationModal(session.uuid));
	};

	if (!event || !channel || !session) return null;
	return (
		<div className="add-comment-container">
			<div>
				{imagePreview ? (
					<div className="image-preview-container">
						<img alt="" src={imagePreview} width="100" height="100" />
						<button className="no-style" onClick={handleRemoveImage}><Icon name={ICONS.CLOSE} color={COLORS.WHITE} size={16} /></button>
					</div>
				) : null}
			</div>
			<>
				{
					selectedGif?.url
						? (
							<div className="gif-preview-container">
								<img src={selectedGif?.url} alt={selectedGif?.title || 'gif'} width="100" height="100" />
								<button className="no-style" onClick={() => setSelectedGif(null)}><Icon name={ICONS.CLOSE} color={COLORS.WHITE} size={16} /></button>
							</div>
						)
						: null
				}
			</>
			<div className="add-comment" onClick={handleNotRegistered}>
				<div
					className={classNames(
						'spamFilterAlert',
						{ showSpamFilterAlert },
					)}
				>
					<div className="spamFilertAlertContent">
						<button className="no-style clear" onClick={() => setShowSpamFilterAlert(false)}><Icon name={ICONS.CLOSE} color={COLORS.WHITE} size={16} /></button>
						<p>{t("BadLanguage")}</p>
					</div>
				</div>
				<TextareaAutosize
					disabled={!!previewMode || isAdmin}
					ref={(tag: any) => (textAreaRef.current = tag)}
					className="add-live-comment-textarea"
					maxRows={5}
					placeholder={t("Send message")}
					maxLength={150}
					value={value}
					onFocus={handleNotRegistered}
					onClick={() => {
						if (!blProfileUser) return handleNotRegistered();
						// use focused index to know where to place the emoji
						if (textAreaRef?.current?.selectionStart) {
							setFocusedIndex(textAreaRef.current.selectionStart);
						}
					}}
					onKeyUp={(e: any) => {
						if (!blProfileUser) return;
						// use focused index to know where to place the emoji
						if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
							setFocusedIndex(e.target.selectionStart);
						}
					}}
					onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
						if (!blProfileUser) return;
						// use focused index to know where to place the emoji
						setFocusedIndex(event.target.selectionStart);
						setValue(event.target.value);
					}}
				/>
				<div className="comment-actions">
					<button id="image-button" disabled={!blProfileUser || !!previewMode || isAdmin} className="no-style x-small center action-trigger-btn" onClick={handleImageTrigger}><Icon name={ICONS.IMAGE} color={COLORS.WHITE} size={15} /></button>
					<input type="file" style={{ display: 'none' }} onChange={handleImageSelect} accept=".jpg,.jpeg,.png,.gif" ref={imageTriggerRef} />
					<div className="giphy-picker-container">
						<button
							disabled={!blProfileUser || !!previewMode || isAdmin}
							className="no-style x-small center action-trigger-btn"
							onClick={() => setShowGiphy(true)}
						><Icon name={ICONS.GIF} color={COLORS.WHITE} size={15} /></button>
						<Popover open={showGiphy} onClose={() => setShowGiphy(false)}>
							{/* need to conditionally render otherwise it does weird selection things when opening and then */}
							{/* clicking the emoji picker, and then re-clicking the giphy select */}
							{showGiphy ? (
								<GiphySelect
									apiKey={"J1L9qUdh9DKn4v22NRHUYhrKZFSxkX3m"}
									searchPlaceholder="Search Gifs"
									onSelect={handleGifSelect}
									// library="stickers" // if we need to use stickers instead of gifs
									masonryConfig={[
										{ columns: 3, imageWidth: 68, gutter: 5 },
									]}
									gifListHeight="260px"
									poweredByGiphy={false}
								/>
							) : null}
						</Popover>
					</div>

					<div className="emoji-picker-container">
						<button disabled={!blProfileUser || !!previewMode || isAdmin} className="no-style x-small center action-trigger-btn" onClick={() => setShowPicker(true)}><Icon name={ICONS.EMOJI_SMILE} color={COLORS.WHITE} size={15} /></button>
						<Popover open={showPicker} onClose={() => setShowPicker(false)}>
							{/* need to conditionally render picker otherwise it does weird selection things */}
							{/* unfortunately this removes the Popover animaation and renders the popover a little slower */}
							{/* but it's not that bad */}
							{showPicker ? (
								<Picker title="" showPreview={false} set="twitter" onSelect={handleEmoji} theme="dark" />
							) : null}
						</Popover>
					</div>

					<button id="send-button" onClick={saveMessage} className="no-style x-small center action-trigger-btn"><Icon name={ICONS.SEND} size={15} color={COLORS.WHITE} /></button>
				</div>
			</div>
		</div>
	);
};

export default AddLiveComment;
